import './styles/ImageHeader.scss';

import { memo, useMemo } from 'react';

import Shadow from '../../../../assets/watch-shadow.svg';
import { cn } from '../../../../helpers/classBuilders';
import { useClassHelper } from '../../../../hooks';
import type { FrameSettings } from '../../../FrameSettingsPanel';
import { isSvg } from '../../helpers';

interface Props {
  url: string;
  alt: string;
  isPortalPreview?: boolean;
  imageConfig?: FrameSettings;
  /** Fires when the header image fails to load (e.g. broken branding URL). */
  onError?: (url: string) => void;
}

const componentName = 'c-ImageHeader';
export const ImageHeader = memo(
  ({ url, alt, isPortalPreview = false, imageConfig, onError }: Props) => {
    const c = useClassHelper(componentName);

    // TODO: Temporary solution alert
    // This function is used to detect whether the image is an SVG or not, this to address a requirement in [UES-6701], by detecting the SVG the layout will be adjusted to fit the image, keeping the compatibility with the predefined images
    // The solution implemented here is a temporary solution suggested by @ronjansen while backend works on make a flexible schema on the branding configuration
    // Why this could be a problem? because it will force all svg images to be full-width, and will not allow centered images similar to the format as the predefined images
    const isSvgImage = isSvg(url);

    const isContainMode = imageConfig?.fitMode === 'contain';
    const xPosition = imageConfig?.xPosition || 'justify-center';
    const yPosition = imageConfig?.yPosition || 'items-center';
    const isCustomFrameHeight = Boolean(imageConfig?.isCustomFrameHeight);
    const isCustomImageSize = Boolean(imageConfig?.isCustomImageSize);
    const isCoverMode = imageConfig?.fitMode === 'cover';
    const coverPosition = imageConfig?.position || 'center';

    const imgStyle = useMemo(() => {
      if (!imageConfig) return {};
      const style: React.CSSProperties = {};
      if (isContainMode) {
        style.height = isCustomImageSize
          ? imageConfig?.customImageSize
          : imageConfig?.imageSize;
      }
      if (isCoverMode) {
        style.objectPosition = coverPosition.replace('-', ' ');
      }
      return style;
    }, [
      imageConfig,
      isContainMode,
      isCustomImageSize,
      isCoverMode,
      coverPosition,
    ]);

    if (imageConfig) {
      return (
        <figure
          className={cn({
            'overflow-hidden': Boolean(imageConfig),
            [`flex ${xPosition} ${yPosition}`]: Boolean(isContainMode),
          })}
          style={{
            height: isCustomFrameHeight
              ? imageConfig?.customFrameHeight
              : imageConfig?.frameHeight,
            backgroundColor: imageConfig?.background,
          }}
        >
          <img
            src={url}
            alt={alt}
            onError={() => onError?.(url)}
            className={cn({
              'object-contain h-auto': isContainMode,
              'object-cover w-full h-full': isCoverMode,
            })}
            style={imgStyle}
          />
        </figure>
      );
    }

    return (
      <section
        className={c('base', '__image_section', {
          '--full-width-image': isSvgImage,
          '--normal-image': !isSvgImage && !isPortalPreview,
          '--is-preview': !isSvgImage && isPortalPreview,
        })}
      >
        <figure className="block mb-6 mt-2 mx-10 relative">
          <img src={url} alt={alt} onError={() => onError?.(url)} />

          {!isSvgImage && (
            <img
              src={Shadow}
              alt="shadow"
              aria-hidden
              className={c('__shadow', {
                '__shadow--is-preview': isPortalPreview,
              })}
            />
          )}
        </figure>
      </section>
    );
  },
);
